import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { GetSiteVisitedDataByCount } from "../Services/APIService";
import { useNavigate, useLocation } from "react-router-dom";

interface Task {
  id: number;
  leadId: number;
  leadName: string | null;
  projectId: number;
  projectName: string | null;
  assignedUser: number;
  assignedUserName: string | null;
  followupTypeId: number;
  followupTypeName: string | null;
  followupDate: string;
  taskNotes: string;
  isPostponed: boolean;
  postponeReason: string | null;
  rescheduleDate: string | null;
  reminderDate: string | null;
  isCompleted: boolean;
  isCanceled: boolean;
  taskCompletionDate: string | null;
  createdBy: number;
  createdByName: string | null;
  createdOn: string;
  modifiedBy: number;
  modifiedByName: string | null;
  modifiedOn: string | null;
}

const SiteVisitedDetails: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Added leadIds and taskIds — populated from URL, used to filter API response
  const [queryParams, setQueryParams] = useState({
    userId: "",
    tenantId: "",
    userName: "",
    leadIds: [] as number[],
    taskIds: [] as number[],
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  // ✅ Parse all params from URL hash — including leadIds and taskIds
  useEffect(() => {
    const hash = window.location.hash;
    const queryString = hash.includes("?") ? hash.split("?")[1] : "";
    const urlParams = new URLSearchParams(queryString);

    const leadIdsRaw = urlParams.get("leadIds") || "";
    const taskIdsRaw = urlParams.get("taskIds") || "";

    setQueryParams({
      userId: urlParams.get("userId") || "",
      tenantId: urlParams.get("tenantId") || "",
      userName: urlParams.get("userName") || "",
      leadIds: leadIdsRaw
        ? leadIdsRaw.split(",").map(Number).filter(Boolean)
        : [],
      taskIds: taskIdsRaw
        ? taskIdsRaw.split(",").map(Number).filter(Boolean)
        : [],
    });
  }, []);

  // Fetch site visited task details
  const fetchSiteVisitedDetails = async () => {
    if (!queryParams.userId || !queryParams.tenantId) return;

    try {
      setLoading(true);
      const response = await GetSiteVisitedDataByCount({
        userId: Number(queryParams.userId),
        tenantId: Number(queryParams.tenantId),
        leadIds: queryParams.leadIds,   // ✅ pass directly to API
        taskIds: queryParams.taskIds,
      });

      setTasks(response || []);
      toast.success("Site visited details loaded successfully");
    } catch (error) {
      console.error("Error fetching site visited details:", error);
      toast.error("Failed to load site visited details");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const leadDashbordRedirection = async (leadId: number) => {
    try {
      navigate("/LeadDashboard", {
        state: { leadId },
      });
    } catch (error) {
      console.error("Error redirecting to lead details:", error);
    }
  };

  useEffect(() => {
    if (queryParams.userId && queryParams.tenantId) {
      fetchSiteVisitedDetails();
    }
  }, [queryParams]);

  // Filter tasks based on search
  const filteredTasks = tasks.filter(
    (task) =>
      task.leadName?.toLowerCase().includes(searchText.toLowerCase()) ||
      task.projectName?.toLowerCase().includes(searchText.toLowerCase()) ||
      task.taskNotes?.toLowerCase().includes(searchText.toLowerCase()) ||
      task.followupTypeName?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Export to Excel
  const exportToExcel = () => {
    try {
      const headers = [
        "Task ID",
        "Lead Name",
        "Project",
        "Follow-up Type",
        "Follow-up Date",
        "Task Notes",
        "Completion Date",
        "Status",
      ];

      const rows = filteredTasks.map((task) => [
        task.id,
        task.leadName || "N/A",
        task.projectName || "N/A",
        task.followupTypeName || "N/A",
        task.followupDate
          ? formatDate(task.followupDate)
          : "N/A",
        task.taskNotes || "N/A",
        task.taskCompletionDate
          ? formatDate(task.taskCompletionDate)
          : "N/A",
        task.isCompleted
          ? "Completed"
          : task.isCanceled
            ? "Canceled"
            : "Pending",
      ]);

      let csvContent = headers.join(",") + "\n";
      rows.forEach((row) => {
        csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `site-visited-details-${queryParams.userName}-${new Date().toISOString().split("T")[0]
        }.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Excel file exported successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Site Visited Details - ${queryParams.userName}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              text-align: center;
              color: #333;
              margin-bottom: 10px;
            }
            .subtitle {
              text-align: center;
              color: #666;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
              font-size: 11px;
            }
            th {
              background-color: #a78590;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .meta-info {
              margin-bottom: 15px;
              font-size: 12px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Site Visited Details Report</h1>
          <div class="subtitle">User: ${queryParams.userName}</div>
          <div class="meta-info">
            <strong>Generated on:</strong> ${new Date().toLocaleString()}<br/>
            <strong>Total Records:</strong> ${filteredTasks.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>Task ID</th>
                <th>Lead Name</th>
                <th>Project</th>
                <th>Follow-up Type</th>
                <th>Follow-up Date</th>
                <th>Task Notes</th>
                <th>Completion Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredTasks
          .map(
            (task) => `
                <tr>
                  <td>${task.id}</td>
                  <td>${task.leadName || "N/A"}</td>
                  <td>${task.projectName || "N/A"}</td>
                  <td>${task.followupTypeName || "N/A"}</td>
                  <td>${task.followupDate
                ? formatDate(task.followupDate)
                : "N/A"
              }</td>
                  <td>${task.taskNotes || "N/A"}</td>
                  <td>${task.taskCompletionDate
                ? formatDate(task.taskCompletionDate)
                : "N/A"
              }</td>
                  <td>${task.isCompleted
                ? "Completed"
                : task.isCanceled
                  ? "Canceled"
                  : "Pending"
              }</td>
                </tr>
              `
          )
          .join("")}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          toast.success("PDF print dialog opened");
        };
      } else {
        toast.error("Please allow pop-ups to export PDF");
      }
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  // const formatDate = (dateString: string | null) => {
  //   if (!dateString) return "N/A";
  //   return new Date(dateString).toLocaleDateString("en-GB", {
  //     day: "2-digit",
  //     month: "short",
  //     year: "numeric",
  //   });
  // };
  const formatDate = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };
  const columns = [
    {
      name: "Lead Name",
      cell: (row: Task) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "rgb(167, 133, 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {row.leadName?.charAt(0).toUpperCase() || "?"}
          </div>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              leadDashbordRedirection(row.leadId);
            }}
            style={{
              textDecoration: "none",
              fontWeight: 500,
              color: "#33475b",
            }}
          >
            <span style={{ fontWeight: "500", color: "#33475b" }}>
              {row.leadName || "N/A"}
            </span>
          </a>
        </div>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Project",
      selector: (row: Task) => row.projectName || "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: "Follow-up Type",
      selector: (row: Task) => row.followupTypeName || "N/A",
      sortable: true,
      width: "140px",
    },
    {
      name: "Follow-up Date",
      selector: (row: Task) => formatDate(row.followupDate),
      sortable: true,
      width: "130px",
    },
    {
      name: "Task Notes",
      selector: (row: Task) => row.taskNotes || "N/A",
      sortable: true,
      width: "200px",
      cell: (row: Task) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={row.taskNotes || "N/A"}
        >
          {row.taskNotes || "N/A"}
        </div>
      ),
    },
    {
      name: "Completion Date",
      selector: (row: Task) => formatDate(row.taskCompletionDate),
      sortable: true,
      width: "140px",
    },
    {
      name: "Status",
      cell: (row: Task) => (
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "12px",
            fontWeight: "600",
            fontSize: "13px",
            backgroundColor: row.isCompleted
              ? "#e8f5e9"
              : row.isCanceled
                ? "#ffebee"
                : "#fff3e0",
            color: row.isCompleted
              ? "#2e7d32"
              : row.isCanceled
                ? "#c62828"
                : "#e65100",
          }}
        >
          {row.isCompleted
            ? "Completed"
            : row.isCanceled
              ? "Canceled"
              : "Pending"}
        </span>
      ),
      width: "150px",
    },
  ];

  return (
    <div className="wrapper">
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 10000 }}
      />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div>
                        <div className="card-title mb-0">
                          Site Visited Details
                        </div>
                        <small style={{ color: "#666" }}>
                          User: {queryParams.userName}
                          {/* ✅ Show lead count next to username */}
                          {queryParams.leadIds.length > 0 && (
                            <span
                              style={{
                                marginLeft: "10px",
                                color: "#2e7d32",
                                fontWeight: 600,
                              }}
                            >
                              · {queryParams.leadIds.length} lead(s)
                            </span>
                          )}
                        </small>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <input
                          type="text"
                          placeholder="Search by Lead, Project or Notes"
                          className="p-2 datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "250px" }}
                        />
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToPDF}
                          title="Export to PDF"
                        >
                          <i className="bi bi-file-pdf"></i> Export PDF
                        </button>
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToExcel}
                          title="Export to Excel"
                        >
                          <i className="bi bi-file-excel"></i> Export Excel
                        </button>
                        <button
                          className="demo-btn btn btn-outline-secondary btn-sm"
                          onClick={() => window.close()}
                          title="Close Window"
                        >
                          <i className="bi bi-x-circle"></i> Close
                        </button>
                      </div>
                    </div>

                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredTasks}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No site visited data found"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>

      <OverlayLoader
        loading={loading}
        message="Loading Site Visited Details"
        submessage="Please wait..."
      />

      <style>{`
        .rdt_TableCol:first-of-type {
          padding-left: 16px !important;
        }
        
        .datatableSearchInput {
          border: 1px solid #dee2e6;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .datatableSearchInput:focus {
          outline: none;
          border-color: #a78590;
          box-shadow: 0 0 0 0.2rem rgba(167, 133, 144, 0.25);
        }
      `}</style>
    </div>
  );
};

export default SiteVisitedDetails;